<template>
  <BaseModal
    v-if="isShowing"
    color="grey"
    rounded
    dropShadow
    class="base-dialog"
    :noPadding="!padding"
    @background-clicked="$emit('close-dialog')"
  >
    <div class="base-dialog__container" :class="padding ? '' : 'padding'">
      <header
        class="base-dialog__header"
        :class="`base-dialog__header--level-${level}`"
      >
        <h1 :class="`base-dialog__heading-${heading}`" v-html="title" />
      </header>
      <section class="base-dialog__body">
        <p v-html="message" />
        <ul v-if="list && list.length > 0" class="base-dialog__body-list">
          <li v-for="(item, index) in list" :key="`dialog-list-item-${index}`">
            {{ item }}
          </li>
        </ul>
        <p v-if="message2" v-html="message2" />
      </section>
      <footer class="base-dialog__footer">
        <button
          type="button"
          class="base-dialog__button base-dialog__button--confirm"
          :class="`base-dialog__button--level-${level}`"
          @click="__confirm()"
        >
          {{ confirmText }}
        </button>
        <button
          v-if="rejectText"
          type="button"
          class="base-dialog__button base-dialog__button--reject"
          @click="__reject()"
        >
          {{ rejectText }}
        </button>
        <button
          v-if="cancelText"
          type="button"
          class="base-dialog__button base-dialog__button--cancel"
          @click="__cancel()"
        >
          {{ cancelText }}
        </button>
      </footer>
    </div>
  </BaseModal>
</template>

<script>
  import BaseModal from './BaseModal.vue';

  export default {
    name: 'BaseDialog',
    components: {
      BaseModal,
    },
    props: {
      size: {
        type: String,
        required: false,
        default: 'md',
        validator: (value) => {
          const SIZES = ['sm', 'md', 'lg'];
          return SIZES.includes(value);
        },
      },
      heading: {
        type: String,
        required: false,
        default: 'h1',
        validator: (heading) => {
          const HEADINGS = ['h1', 'h2', 'h3'];
          return HEADINGS.includes(heading);
        },
      },
      padding: {
        type: Boolean,
        required: false,
        default: true,
      },
    },
    data: () => ({
      isShowing: false,
      resolvePromise: undefined,
      rejectPromise: undefined,
      title: '',
      message: '',
      confirmText: '',
      cancelText: '',
      rejectText: '',
      level: '',
      list: [],
      message2: '',
    }),
    methods: {
      prompt({
        title = '',
        message = '',
        confirmText = 'Ok',
        rejectText = '',
        cancelText = '',
        level = 'default',
        list = [],
        message2 = '',
      }) {
        this.title = title;
        this.message = message;
        this.confirmText = confirmText;
        this.cancelText = cancelText;
        this.rejectText = rejectText;
        this.level = level;
        this.list = list;
        this.message2 = message2;

        this.isShowing = true;

        return new Promise((resolve, reject) => {
          this.resolvePromise = resolve;
          this.rejectPromise = reject;
        });
      },
      __confirm() {
        this.isShowing = false;
        this.resolvePromise(true);
      },
      __reject() {
        this.isShowing = false;
        this.rejectPromise(true);
      },
      __cancel() {
        this.isShowing = false;
        this.resolvePromise(false);
      },
    },
  };
</script>

<style lang="scss" scoped>
  :deep(.base-modal__content) {
    overflow: hidden;
  }
  .base-dialog {
    &__container {
      height: 100%;
      max-height: 80vh;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      padding: 0;
      margin: 0;
    }
    &__container.padding {
      padding: 32px 32px 17px 32px;
    }

    &__header {
      display: flex;
      justify-content: center;
      background-color: transparent;
      width: 100%;
      text-align: center;
    }
    &__heading {
      &-h1 {
        font-size: 26px;
        font-weight: 600;
      }
      &-h2 {
        font-size: 20px;
        font-weight: 600;
      }
      &-h3 {
        font-size: 16px;
        font-weight: 600;
      }
    }
    &__body {
      flex: 1 auto;
      background-color: transparent;
      width: 100%;
      padding: 25px 0;
      overflow-y: auto;
    }
    &__body-list {
      color: #585b60;
      font-weight: 700;
    }
    &__footer {
      display: flex;
      justify-content: center;
      align-content: center;
      gap: 25px;

      background-color: transparent;
      padding: 15px;
      width: 100%;
    }

    &__button {
      padding: 10px 25px;
      border-radius: 6px;
      width: 100%;
      max-width: 225px;
      text-transform: uppercase;
      cursor: pointer;
      font-weight: 600;
      &:hover {
        transition:
          background-color $default-transition-time,
          color $default-transition-time,
          border-radius $default-transition-time;
        border-radius: 8px;
      }
      &--level-default {
        background-color: #cccccc;
        color: var(--itr-blue);
        &:hover {
          background-color: darken(#cccccc, 15%);
        }
      }
      &--level-success {
        background-color: #09a684;
        color: #f1f2f3;
        &:hover {
          background-color: #069370;
        }
      }
      &--level-warning {
        background-color: #ff9800;
        color: #f1f2f3;
        &:hover {
          background-color: darken(#ff9800, 15%);
        }
      }
      &--level-danger {
        background-color: #9d0404;
        color: #f1f2f3;
        &:hover {
          background-color: lighten(#9d0404, 15%);
        }
      }
      &--level-itr-green {
        background-color: var(--itr-green-mid);
        color: #f1f2f3;
        &:hover {
          background-color: #069370;
        }
      }

      &--confirm {
      }

      &--reject {
      }
      &--cancel {
        background-color: var(--itr-charcoal);
        color: #f1f2f3;
        &:hover {
          background-color: var(--itr-grey-mid);
        }
      }
    }
  }

  .bold {
    font-weight: 700;
  }
</style>
